import { useState, useEffect, useCallback, useRef } from 'react';
import { CheckBox, List, Popup, Tooltip, TabPanel, DataGrid, LoadIndicator } from 'devextreme-react';
import { Column } from 'devextreme-react/data-grid';
import { Item } from 'devextreme-react/tab-panel';
import { Button, TextBox } from 'devextreme-react';
import { useScreenSize } from '../../../../../utils/media-query';
import { useAppSelector } from '../../../../../contexts/hooks';
import { newToast } from '../../../..';
import { ToolbarItem } from 'devextreme-react/popup';
import { SendResultByWhatsApp } from '../../../../../types/types.orders';
import { UseApiResults } from '../../../../../api/api.results';
import './SendWhatsAppResults.scss';

interface WhatsAppConversation {
  Wp_Id: number;
  Wp_Pc_Id: number;
  Wp_Tipo: number;
  Wp_Emisor: number;
  Wp_Fecha: string;
  Wp_Numero: string;
  Wp_Mensaje: string;
}

interface SendWhatsAppResultsProps {
  visible: boolean;
  setVisible: ({ visible }) => void;
  lstStudies: number[] | undefined;
  patientData?: any; // Tipo específico según tu aplicación
  orderData?: any; // Tipo específico según tu aplicación
}

export const SendWhatsAppResults = ({ visible, setVisible, lstStudies, patientData: propPatientData, orderData: propOrderData }: SendWhatsAppResultsProps) => {
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const WhatsAppServiceFieldPatient = useAppSelector((x) => x.configuration.Data?.Config_General.find((cfg) => cfg.Cgn_Nombre === 'WhatsAppFieldPatient')?.Cgn_Resultado);
  const WhatsAppFieldMask = useAppSelector((x) => x.configuration.Data?.Config_General.find((cfg) => cfg.Cgn_Nombre === 'WhatsAppFieldMask')?.Cgn_Resultado);
  const info = useAppSelector((x) => x.configuration.Data?.SystemFields);
  const globalOrderData = useAppSelector((x) => x.globalOrders.CurrentOrder.OrderData);
  const globalPatientData = useAppSelector((x) => x.globalOrders.CurrentOrder.PatientData);
  const attachments = useAppSelector((x) => x.globalOrders.CurrentOrder.Attachments);
  const [attachmentsStudies, setAttachmentsStudies] = useState(true);
  const [attachment, setAttachment] = useState(true);
  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [phoneInput, setPhoneInput] = useState<string>('');
  const { senResultByWhatsApp, getConversationWhatsApp } = UseApiResults();
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [conversations, setConversations] = useState<WhatsAppConversation[]>([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [conversationsLoaded, setConversationsLoaded] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible) return;

    // Usar los datos proporcionados por props o caer en los datos globales
    const effectiveOrderData = propOrderData || globalOrderData;
    const effectivePatientData = propPatientData || globalPatientData;

    const matchedField = info?.find((field) => field.Dfc_Equivalencia === WhatsAppServiceFieldPatient);

    if (matchedField) {
      if (matchedField.Dfc_Tabla === 'Ordenes' && effectiveOrderData?.AditionalFields) {
        const phoneList = effectiveOrderData.AditionalFields[matchedField.Dfc_Equivalencia] || [];
        const formattedPhones = Array.isArray(phoneList) ? phoneList : phoneList ? [phoneList] : [];
        // Tomar solo el primer número si existe
        const firstPhone = formattedPhones.length > 0 ? formatPhoneWithMask(formattedPhones[0]) : '';
        setPhoneNumber(firstPhone);
      } else if (matchedField.Dfc_Tabla === 'Pacientes' && effectivePatientData?.AditionalFields) {
        const phoneList = effectivePatientData.AditionalFields[matchedField.Dfc_Equivalencia] || [];
        const formattedPhones = Array.isArray(phoneList) ? phoneList : phoneList ? [phoneList] : [];
        // Tomar solo el primer número si existe
        const firstPhone = formattedPhones.length > 0 ? formatPhoneWithMask(formattedPhones[0]) : '';
        setPhoneNumber(firstPhone);
      } else {
        setPhoneNumber('');
      }
    } else {
      setPhoneNumber('');
    }
  }, [visible, propPatientData, propOrderData]);

  const handleClose = () => {
    setVisible({ visible: false });
    setSelectedTabIndex(0);
    setConversationsLoaded(false);
  };

  const loadConversations = useCallback(async () => {
    if (!idOrd || idOrd === -1) return;

    setLoadingConversations(true);
    try {
      const data = await getConversationWhatsApp(idOrd);
      if (Array.isArray(data)) {
        setConversations(data);
        // Scroll al final después de cargar
        setTimeout(() => {
          chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        setConversations([]);
      }
      setConversationsLoaded(true);
    } catch (error) {
      newToast('ERROR', 'Error al cargar el historial de conversaciones', 5);
      setConversations([]);
    } finally {
      setLoadingConversations(false);
    }
  }, [idOrd, getConversationWhatsApp]);

  const handleTabChange = useCallback(
    (e: any) => {
      const newIndex = e.addedItems && e.addedItems.length > 0 ? e.component.option('selectedIndex') : selectedTabIndex;

      setSelectedTabIndex(newIndex);

      // Cargar conversaciones solo cuando se visita el tab de historial
      if (newIndex === 1 && !conversationsLoaded && idOrd && idOrd !== -1) {
        loadConversations();
      }
    },
    [conversationsLoaded, idOrd, loadConversations, selectedTabIndex]
  );

  const handleAddPhone = () => {
    const trimmedPhone = phoneInput.trim();
    if (trimmedPhone && validatePhone(trimmedPhone)) {
      // Asegurar que el valor tiene la máscara aplicada completamente
      const formattedPhone = formatPhoneWithMask(trimmedPhone);
      setPhoneNumber(formattedPhone);
      setPhoneInput('');
    } else {
      newToast('WARNING', 'El número de teléfono no es válido.', 5);
    }
  };

  // Función para eliminar el número de teléfono
  const handleRemovePhone = () => {
    setPhoneNumber('');
  };

  const handleChangeAttachmentStudies = (e: any) => {
    setAttachmentsStudies(e);
  };

  const handleChangeAttachment = (e: any) => {
    setAttachment(e);
  };

  // Función para formatear números por defecto con máscara
  const formatPhoneWithMask = (phoneValue: string): string => {
    if (!phoneValue || !WhatsAppFieldMask) return phoneValue;

    // Si el número ya tiene formato de máscara, devolverlo tal como está
    if (phoneValue.includes('+') && phoneValue.includes('(')) {
      return phoneValue;
    }

    // Aplicar la máscara manualmente
    return applyMaskToPhone(phoneValue, WhatsAppFieldMask);
  };

  // Función para aplicar la máscara completa al número de teléfono
  const applyMaskToPhone = (phoneValue: string, mask: string): string => {
    if (!mask) return phoneValue;

    // Extraer solo los dígitos del valor ingresado
    const digits = phoneValue.replace(/\D/g, '');

    // Aplicar la máscara caracter por caracter
    let result = '';
    let digitIndex = 0;

    for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
      const maskChar = mask[i];

      if (maskChar === '9' || maskChar === '0') {
        // Es un placeholder para dígito
        result += digits[digitIndex];
        digitIndex++;
      } else {
        // Es un caracter literal de la máscara
        result += maskChar;
      }
    }

    return result;
  };

  // Función para generar placeholder basado en la máscara
  const getPlaceholder = () => {
    if (WhatsAppFieldMask) {
      return `Formato: ${WhatsAppFieldMask}`;
    }
    return 'Escribe un número de teléfono';
  };

  const validatePhone = (phone: string) => {
    // Si hay máscara configurada, usar validación más flexible
    if (WhatsAppFieldMask) {
      // Extraer solo los dígitos del valor ingresado
      const cleanPhone = phone.replace(/\D/g, '');
      // Contar cuántos dígitos requiere la máscara
      const maskDigitCount = WhatsAppFieldMask.replace(/[^\d90]/g, '').length;
      // Validar que tenga al menos la cantidad mínima de dígitos esperados
      return cleanPhone.length >= Math.min(7, maskDigitCount);
    }

    // Validación básica para números de teléfono (puede ajustarse según necesidades)
    const phoneRegex = /^[\+]?[\d\s\-\(\)]{7,15}$/;
    return phoneRegex.test(phone);
  };

  const onSaveClick = () => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (!phoneNumber) {
      newToast('WARNING', 'Debe ingresar un número de teléfono de destino', 5);
      return;
    }

    const dataToSend: SendResultByWhatsApp = {
      IdOrder: idOrd,
      PhoneDestinator: phoneNumber,
      Studies: lstStudies || [],
      IncludeAttachments: attachment,
      IncludeAttachmentsStudies: attachmentsStudies,
    };

    newToast('INFO', 'Se está enviando por WhatsApp, esto puede tardar algunos minutos.', 5);
    senResultByWhatsApp(dataToSend);

    setVisible({ visible: false });
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPatientName = () => {
    const patientData = propPatientData || globalPatientData;
    if (patientData?.Pc_Nombres && patientData?.Pc_Apellidos) {
      return `${patientData.Pc_Nombres} ${patientData.Pc_Apellidos}`;
    }
    return 'Cliente';
  };

  const renderEmisor = (data: any) => {
    return data.Wp_Emisor === 1 ? 'Synerlab' : 'Cliente';
  };

  return (
    <Popup visible={visible} fullScreen={isMedium || isSmall || isXSmall} width={800} maxHeight={700} title='WhatsApp - Resultados' onHiding={handleClose} showCloseButton={true}>
      <TabPanel selectedIndex={selectedTabIndex} onSelectionChanged={handleTabChange} swipeEnabled={false} animationEnabled={true}>
        <Item title='Enviar Resultados'>
          <div style={{ padding: '20px' }}>
            <div>
              {/* Mostrar campo de agregar solo si no hay número */}
              {!phoneNumber && (
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <TextBox id='phoneInput' value={phoneInput} onValueChanged={(e) => setPhoneInput(e.value)} label='Agregar teléfono' labelMode='static' placeholder={getPlaceholder()} onEnterKey={handleAddPhone} style={{ flex: 1, marginRight: '8px' }} mask={WhatsAppFieldMask || undefined} maskInvalidMessage='Formato de teléfono inválido' />
                  <Button stylingMode='text' icon='add' onClick={handleAddPhone} type='default' />
                </div>
              )}

              {/* Mostrar número actual con opción de eliminar */}
              {phoneNumber && (
                <div className='widget-container'>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <TextBox value={phoneNumber} readOnly={true} label='Teléfono de destino' labelMode='static' style={{ flex: 1, marginRight: '8px' }} />
                    <Button stylingMode='text' icon='remove' onClick={handleRemovePhone} type='danger' />
                  </div>
                </div>
              )}
            </div>
            {attachments && attachments.length > 0 && (
              <>
                <hr />
                <h5>Incluir archivos adjuntos?</h5>
                <p style={{ marginTop: -20 }}>Esta orden contiene archivos adjuntos que pueden ser enviados por WhatsApp.</p>
              </>
            )}
            {attachments && attachments.filter((x) => x.Oad_Es_Id).length > 0 && (
              <div className='widget-container'>
                <div className='dx-field' id='attachmentsest'>
                  <div className='dx-field-label'>Incluir adjuntos de estudios?</div>
                  <div className='dx-field-value'>
                    <CheckBox defaultValue={true} elementAttr={{ 'aria-label': 'Checked' }} onValueChange={handleChangeAttachmentStudies} />
                  </div>
                </div>
                <Tooltip target={'#attachmentsest'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Adjuntos que están asociados a un estudio.
                </Tooltip>
              </div>
            )}

            {attachments && attachments.filter((x) => !x.Oad_Es_Id).length > 0 && (
              <div className='widget-container' style={{ marginTop: -15 }}>
                <div className='dx-field' id='attachments'>
                  <div className='dx-field-label'>Incluir archivos adjuntos no asociados a estudios?</div>
                  <div className='dx-field-value'>
                    <CheckBox defaultValue={true} elementAttr={{ 'aria-label': 'Checked' }} onValueChange={handleChangeAttachment} />
                  </div>
                </div>
                <Tooltip target={'#attachments'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Adjuntos que no están asociados a un estudio.
                </Tooltip>
              </div>
            )}
          </div>
        </Item>

        <Item title='Historial de Conversaciones'>
          <div className='whatsapp-chat-container'>
            {loadingConversations ? (
              <div className='chat-loading'>
                <LoadIndicator visible={true} />
              </div>
            ) : conversations.length === 0 ? (
              <div className='chat-messages'>
                <div className='no-messages'>No hay conversaciones registradas</div>
              </div>
            ) : (
              <div className='chat-messages'>
                {conversations.map((msg) => (
                  <div key={msg.Wp_Id} className={`message-wrapper ${msg.Wp_Emisor === 1 ? 'synerlab' : 'client'}`}>
                    <div className='message-bubble'>
                      <div className='message-header'>{msg.Wp_Emisor === 1 ? 'SynerLab' : getPatientName()}</div>
                      <div className='message-text'>{msg.Wp_Mensaje}</div>
                      <div className='message-time'>{formatTime(msg.Wp_Fecha)}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>
        </Item>
      </TabPanel>
      {selectedTabIndex === 0 && (
        <ToolbarItem toolbar='bottom' location='after'>
          <div className={`form-popup-buttons-container `}>
            <Button stylingMode='contained' text='Cancelar' type={'normal'} onClick={handleClose} />
            <Button stylingMode='contained' text='Enviar' type={loading ? 'normal' : 'default'} disabled={loading} onClick={onSaveClick} />
          </div>
        </ToolbarItem>
      )}
    </Popup>
  );
};
